import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { DrawerProps } from 'antd/lib/drawer';

import { DrawerHeader } from '~/ui/components/Drawer/DrawerHeader';
import { DrawerBody, DrawerDefault } from '~/ui/components/Drawer/Drawer.styled';

type Config = DrawerProps & {
  width?: number;
  extraAction?: ReactNode;
  noPadding?: boolean;
  backButton?: boolean;
  onBackClick?: () => void;
  sideBySide?: boolean;
  scrollbar?: boolean;
};

interface Drawer {
  component?: any;
  config: Config;
  opened: boolean;
  id: string;
  loading?: boolean;
  instance?: number;
}

interface DrawerContextData {
  openDrawer(id: string, component: any, config?: Config): void;
  closeDrawer(id: string): void;
  closeAllDrawers(): void;
  setConfig(id: string, config?: Config, replace?: boolean): void;
  setLoading(id: string, loading: boolean): void;
}

const defaultConfig: DrawerProps = {
  closable: false,
};

const DrawerContext = createContext<DrawerContextData>({} as DrawerContextData);

function resolveInstance(drawers: Drawer[], id: string): number {
  const existing = drawers.find((el) => el?.id === id);

  if (!existing) return 0;

  return existing.opened ? existing.instance || 0 : (existing.instance || 0) + 1;
}

function DrawerProvider({ children }: PropsWithChildren<{}>) {
  const [drawers, setDrawers] = useState<Drawer[]>([]);

  const closeAllDrawers = () => {
    setDrawers((currentDrawers) => currentDrawers.map((drawer) => ({ ...drawer, opened: false })));
  };

  const closeDrawer = (id: string) => {
    setDrawers((currentDrawers) => {
      // Se for um drawer sideBySide, ao fechar o Drawer primário, o drawer secundário também será fechado
      if (currentDrawers?.[0]?.config?.sideBySide && currentDrawers?.[0]?.id === id) {
        return currentDrawers.map((drawer) => ({ ...drawer, opened: false }));
      }

      return currentDrawers.map((drawer) => {
        if (drawer?.id !== id) return drawer;
        return { ...drawer, opened: false };
      });
    });
  };

  const setConfig = (id: string, config?: Config, replace?: boolean) => {
    setDrawers((currentDrawers) =>
      currentDrawers.map((drawer) => {
        if (drawer.id !== id) return drawer;
        return {
          ...drawer,
          config: replace ? config : { ...drawer.config, ...config },
        };
      }),
    );
  };

  const setLoading = (id: string, loading?: boolean) => {
    setDrawers((currentDrawers) =>
      currentDrawers.map((drawer) => {
        if (drawer.id !== id) return drawer;
        return {
          ...drawer,
          loading,
          config: {
            ...drawer.config,
            maskClosable: loading ? false : drawer.config?.maskClosable,
            keyboard: loading ? false : drawer.config?.keyboard,
          },
        };
      }),
    );
  };

  const openDrawer = useCallback(
    (id: string, component: any, config?: Config) => {
      setDrawers((currentDrawers) => {
        let restOfDrawers = currentDrawers.filter((el) => el?.id !== id);
        const instance = resolveInstance(currentDrawers, id);

        if (config?.sideBySide) {
          if (restOfDrawers.length >= 2) {
            restOfDrawers = restOfDrawers.slice(0, 1);
          }

          const newDrawer = {
            id,
            opened: true,
            component,
            config,
            instance,
          };

          // Substituir o drawer existente com as novas informações
          return [...restOfDrawers, newDrawer];
        }

        const existingDrawer = currentDrawers.find((el) => el.id === id);

        const drawer = existingDrawer
          ? { ...existingDrawer, opened: true, component, config, instance }
          : { id, opened: true, component, config, instance };

        return [...restOfDrawers, drawer];
      });
    },
    [setDrawers],
  );

  const handleOnClose = (drawer) => {
    if (drawer?.config?.onClose) {
      drawer?.config?.onClose();
    }

    closeDrawer(drawer?.id);
  };

  useEffect(() => {
    if (!drawers.some((el) => el?.opened === false)) return undefined;
    const timeout = setTimeout(() => {
      setDrawers((currentValue) => currentValue.filter((el) => el?.opened !== false));
    }, 500);
    return () => clearTimeout(timeout);
  }, [drawers]);

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer, closeAllDrawers, setConfig, setLoading }}>
      {children}
      {drawers.map((drawer, index) => {
        const { extraAction, noPadding, backButton, onBackClick, sideBySide, ...config } = drawer?.config || {};
        const nextDrawerWidth = drawers[index + 1]?.config?.width;
        const hasSideDrawer = sideBySide && index === 0 && drawers.length > 1;
        const isSecondDrawer = sideBySide && index !== 0;
        const { scrollbar } = config;

        return (
          <DrawerDefault
            key={`${drawer?.id}-${drawer?.instance || 0}`}
            {...defaultConfig}
            {...config}
            width={config?.width ? `min(${config?.width}px, 100%)` : null}
            visible={drawer?.opened}
            onClose={() => handleOnClose(drawer)}
            className={`mt-drawer ${noPadding ? 'no-padding' : ''} ${drawer?.loading ? 'loading' : ''} ${
              config.className
            }`}
            marginWidth={nextDrawerWidth}
            $secondDrawerWidth={drawer?.config?.width}
            $hasSideDrawer={hasSideDrawer}
            $isSquareBorder={isSecondDrawer}
            mask={index === 0}
            scrollbar={scrollbar}
          >
            {drawer?.config?.title && (
              <DrawerHeader
                title={config?.title}
                closable={config?.closable}
                onClose={() => handleOnClose(drawer)}
                extraAction={extraAction}
                backButton={backButton}
                onBackClick={onBackClick}
              />
            )}
            <DrawerBody className="mt-drawer-body">{drawer?.component}</DrawerBody>
          </DrawerDefault>
        );
      })}
    </DrawerContext.Provider>
  );
}

function useDrawer(): DrawerContextData {
  const context = useContext(DrawerContext);

  if (!context) {
    throw new Error('useDrawer must be used inside of DrawerProvider');
  }

  return context;
}

export { DrawerProvider, useDrawer };
